import type { CharacterKind } from '@/components'

/**
 * The fighters.
 *
 * The previous roster was six sets of proportions — thin, fat, tall, short,
 * blocky, noodly — and it was rejected outright by the player, correctly. The
 * measurement behind that: the widest and the narrowest of them differed by
 * 0.02 m across the torso, which is **one pixel** on this camera, while the
 * coloured glow under a fighter's feet is 128 px. Proportions cannot carry
 * identity here, and stretching a capsule is not a character.
 *
 * So each fighter now owns a *feature*: a visor, a shell, a beak, a pair of
 * horns. A silhouette element reads instantly and from any angle, which is the
 * only thing that works at twenty-two pixels tall in peripheral vision — the
 * same conclusion Rocket League reached with hats and Party Animals with animal
 * species, both of which keep one set of proportions underneath.
 *
 * Nothing here touches the collider: appearance is never a competitive choice.
 * See docs/CHARACTERS.md §6.
 */
export interface BodyPlan {
  id: CharacterKind
  name: string
  blurb: string
  /** Overall height multiplier; built from segment lengths, never a scale. */
  height: number
  /** Head radius. */
  head: number
  /** Head silhouette. */
  headShape: 'sphere' | 'box' | 'egg'
  /**
   * Torso construction. Not a proportion — a different object: a stack of
   * crates does not read as a fat capsule, it reads as a different creature.
   */
  torso: 'capsule' | 'box' | 'stack' | 'ovoid'
  /**
   * What it walks on. `digitigrade` bends the knee backwards like a bird's,
   * `none` means it does not walk at all — it hovers, and the whole locomotion
   * branch changes with it.
   */
  legs: 'normal' | 'digitigrade' | 'none'
  /** What hangs from the shoulders: two arms, four, a pair of wings, or none. */
  arms: 'pair' | 'four' | 'wings' | 'stubs'
  /** Forward tilt of the whole body at rest, radians. A hunch is a character. */
  hunch: number
  /** Height a legless fighter floats at, in torso lengths. */
  hover: number
  /** The one feature that names this fighter across the arena. */
  feature: 'visor' | 'shell' | 'beak' | 'plate' | 'horns' | 'none'
  /** Shape of the crown token: sides of the polygon, 24 reads as a circle. */
  token: number
  /** Torso width and depth. */
  bodyWidth: number
  /** Torso length. */
  bodyLength: number
  /** Thickness of arms and legs. */
  limb: number
  /** Leg length; short legs read as stocky, long ones as leggy. */
  legLength: number
  armLength: number
  /** Distance between the feet. */
  stance: number
  /** Walk-cycle rate; heavier fighters plod, small ones scurry. */
  gaitRate: number
  /** Swing amplitude of the limbs. */
  swing: number
}

const BASE: Omit<
  BodyPlan,
  | 'id'
  | 'name'
  | 'blurb'
  | 'headShape'
  | 'feature'
  | 'token'
  | 'torso'
  | 'legs'
  | 'arms'
> = {
  hunch: 0,
  hover: 0,
  height: 1,
  head: 0.3,
  bodyWidth: 0.26,
  bodyLength: 0.42,
  limb: 0.085,
  legLength: 0.44,
  armLength: 0.4,
  stance: 0.16,
  gaitRate: 1,
  swing: 1,
}

export const BODY_PLANS: Record<CharacterKind, BodyPlan> = {
  guard: {
    ...BASE,
    id: 'guard',
    name: 'БРОНЯ',
    blurb: 'Косматая громадина. Бьёт тяжелее всех и держит стойку лучше всех.',
    headShape: 'box',
    torso: 'box',
    legs: 'normal',
    arms: 'pair',
    feature: 'visor',
    token: 4,
    head: 0.3,
    bodyWidth: 0.32,
    bodyLength: 0.44,
    limb: 0.1,
    legLength: 0.4,
    gaitRate: 0.82,
    swing: 0.9,
  },
  beetle: {
    ...BASE,
    id: 'beetle',
    name: 'ХВАТ',
    blurb: 'Приземистая и вертлявая. Три заряда рывка, слабый толчок.',
    headShape: 'sphere',
    torso: 'ovoid',
    legs: 'normal',
    arms: 'four',
    hunch: 0.34,
    feature: 'shell',
    token: 6,
    height: 0.9,
    head: 0.28,
    bodyWidth: 0.38,
    bodyLength: 0.34,
    limb: 0.095,
    legLength: 0.3,
    armLength: 0.32,
    stance: 0.22,
    gaitRate: 1.45,
    swing: 0.85,
  },
  crane: {
    ...BASE,
    id: 'crane',
    name: 'ЕГЕРЬ',
    blurb: 'Держит дистанцию: быстрый снаряд и широкая волна.',
    headShape: 'egg',
    torso: 'capsule',
    legs: 'digitigrade',
    arms: 'wings',
    feature: 'beak',
    token: 3,
    height: 1.18,
    head: 0.24,
    bodyWidth: 0.21,
    bodyLength: 0.4,
    limb: 0.062,
    legLength: 0.6,
    armLength: 0.5,
    stance: 0.13,
    gaitRate: 0.75,
    swing: 1.2,
  },
  brick: {
    ...BASE,
    id: 'brick',
    name: 'ПРОРАБ',
    blurb: 'Медленный и тяжёлый. Попал — вынес, промахнулся — стоишь.',
    headShape: 'box',
    torso: 'stack',
    legs: 'normal',
    arms: 'pair',
    feature: 'plate',
    token: 5,
    head: 0.29,
    bodyWidth: 0.36,
    bodyLength: 0.44,
    limb: 0.11,
    legLength: 0.36,
    armLength: 0.36,
    stance: 0.22,
    gaitRate: 0.88,
    swing: 0.8,
  },
  jester: {
    ...BASE,
    id: 'jester',
    name: 'ЗНАХАРЬ',
    blurb: 'Ловушечник: воронка вдвое чаще и шире, но бьёт слабее всех.',
    headShape: 'sphere',
    torso: 'capsule',
    legs: 'normal',
    arms: 'pair',
    hunch: 0.42,
    feature: 'horns',
    token: 8,
    height: 1.05,
    head: 0.26,
    bodyWidth: 0.2,
    limb: 0.06,
    legLength: 0.5,
    armLength: 0.46,
    stance: 0.14,
    gaitRate: 1.3,
    swing: 1.3,
  },
  blob: {
    ...BASE,
    id: 'blob',
    name: 'БРОДЯГА',
    blurb: 'Волна вокруг себя — его главный приём.',
    headShape: 'sphere',
    torso: 'ovoid',
    legs: 'none',
    arms: 'stubs',
    hover: 0.9,
    feature: 'none',
    token: 24,
    height: 0.85,
    head: 0.36,
    bodyWidth: 0.34,
    bodyLength: 0.26,
    limb: 0.08,
    legLength: 0.22,
    armLength: 0.24,
    stance: 0.18,
    gaitRate: 1.6,
    swing: 0.95,
  },
}

export const CHARACTER_KINDS: CharacterKind[] = [
  'guard',
  'beetle',
  'crane',
  'brick',
  'jester',
  'blob',
]
